/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Plus, Trash2, Shield, Loader2, Info } from 'lucide-react';
import { Button } from '../../ui/Button';
import { useWallet } from '../../../hooks/useWallet';
import { Token } from '../../../types';

interface NewEscrowModalProps {
    onClose: () => void;
    onComplete: () => void;
}

interface Milestone {
    title: string;
    amount: string;
}

export const NewEscrowModal: React.FC<NewEscrowModalProps> = ({ onClose, onComplete }) => {
    const { balances } = useWallet();
    const [step, setStep] = useState<'details' | 'milestones' | 'review'>('details');
    const [isProcessing, setIsProcessing] = useState(false);

    // Step 1: Details
    const [title, setTitle] = useState('');
    const [counterparty, setCounterparty] = useState('');
    const [chain, setChain] = useState<'SOL' | 'SUI'>('SUI');
    const [token, setToken] = useState<Token | null>(null);
    const [totalAmount, setTotalAmount] = useState('');
    const [stealth, setStealth] = useState(true);

    // Step 2: Milestones
    const [milestones, setMilestones] = useState<Milestone[]>([{ title: 'Initial Milestone', amount: '' }]);

    const chainTokens = useMemo(() => {
        const filtered = balances.filter(b => b.chain === chain);
        if (token && token.chain !== chain) {
            setToken(null);
        }
        return filtered;
    }, [balances, chain, token]);

    const handleMilestoneChange = (index: number, field: 'title' | 'amount', value: string) => {
        const newMilestones = [...milestones];
        newMilestones[index][field] = value;
        setMilestones(newMilestones);
    };

    const addMilestone = () => {
        setMilestones([...milestones, { title: '', amount: '' }]);
    };

    const removeMilestone = (index: number) => {
        if (milestones.length > 1) {
            setMilestones(milestones.filter((_, i) => i !== index));
        }
    };
    
    const milestoneTotal = useMemo(() => {
        return milestones.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
    }, [milestones]);

    const handleDeploy = async () => {
        setIsProcessing(true);
        // Simulate deployment
        await new Promise(res => setTimeout(res, 2000));
        setIsProcessing(false);
        onComplete();
    };

    const isDetailsValid = title && counterparty && token && totalAmount && parseFloat(totalAmount) > 0;
    const areMilestonesValid = milestones.every(m => m.title && m.amount && parseFloat(m.amount) >= 0) && parseFloat(totalAmount) === milestoneTotal;

    const renderContent = () => {
        switch (step) {
            case 'details':
                return (
                    <div>
                        <h3 className="font-serif text-2xl text-white mb-6">New Escrow Contract</h3>
                        <div className="space-y-4">
                             <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Contract Title</label>
                                <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full mt-1 p-3 bg-white/5 text-white rounded-lg border border-white/10 focus:border-nobel-gold outline-none" placeholder="e.g., Q4 Marketing Campaign" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Counterparty Address</label>
                                <input type="text" value={counterparty} onChange={e => setCounterparty(e.target.value)} className="w-full mt-1 p-3 bg-white/5 text-white rounded-lg border border-white/10 focus:border-nobel-gold outline-none" placeholder="SOL or SUI address" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Chain</label>
                                    <select value={chain} onChange={e => setChain(e.target.value as 'SOL' | 'SUI')} className="w-full mt-1 p-3 bg-white/5 text-white rounded-lg border border-white/10 focus:border-nobel-gold outline-none appearance-none">
                                        <option value="SUI">Sui</option>
                                        <option value="SOL">Solana</option>
                                    </select>
                                </div>
                                 <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Token</label>
                                    <select value={token?.mint || ''} onChange={e => setToken(chainTokens.find(t => t.mint === e.target.value) || null)} className="w-full mt-1 p-3 bg-white/5 text-white rounded-lg border border-white/10 focus:border-nobel-gold outline-none appearance-none">
                                        <option value="" disabled>Select Token</option>
                                        {chainTokens.map(t => <option key={t.mint} value={t.mint}>{t.symbol}</option>)}
                                    </select>
                                </div>
                            </div>
                             <div>
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Amount</label>
                                <input type="number" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} className="w-full mt-1 p-3 bg-white/5 text-white rounded-lg border border-white/10 focus:border-nobel-gold outline-none" placeholder="0.00" />
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <label className="flex items-center gap-2 text-gray-400 cursor-pointer text-sm">
                                    <input type="checkbox" checked={stealth} onChange={e => setStealth(e.target.checked)} className="rounded-sm accent-nobel-gold" />
                                    <Shield size={16} className="text-nobel-gold" /> Use Stealth Address
                                </label>
                            </div>
                        </div>
                        <div className="pt-6 mt-6 border-t border-white/10">
                            <Button onClick={() => setStep('milestones')} disabled={!isDetailsValid} className="w-full">Define Milestones</Button>
                        </div>
                    </div>
                );
            case 'milestones':
                 return (
                    <div>
                         <div className="flex items-center gap-4 mb-6">
                            <button onClick={() => setStep('details')} className="text-gray-400 hover:text-white"><ArrowLeft size={20}/></button>
                            <h3 className="font-serif text-2xl text-white">Define Milestones</h3>
                        </div>
                        <div className="space-y-4 max-h-80 overflow-y-auto custom-scrollbar pr-2 -mr-2">
                            {milestones.map((m, i) => (
                                <div key={i} className="flex gap-3 items-start p-3 bg-white/5 rounded-lg">
                                    <span className="font-bold text-gray-400 pt-2.5">{i+1}.</span>
                                    <div className="flex-1 space-y-2">
                                        <input type="text" placeholder="Milestone Title" value={m.title} onChange={e => handleMilestoneChange(i, 'title', e.target.value)} className="w-full p-2 bg-black/30 text-white rounded-md border border-white/10" />
                                        <input type="number" placeholder="Amount" value={m.amount} onChange={e => handleMilestoneChange(i, 'amount', e.target.value)} className="w-full p-2 bg-black/30 text-white rounded-md border border-white/10" />
                                    </div>
                                    <button onClick={() => removeMilestone(i)} disabled={milestones.length <= 1} className="p-2 text-gray-500 hover:text-red-500 disabled:opacity-50 mt-2.5"><Trash2 size={16}/></button>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between items-center mt-4">
                            <Button variant="outline" size="sm" onClick={addMilestone} icon={<Plus size={14}/>}>Add Milestone</Button>
                            <div className="text-right text-sm">
                                <div className={`font-bold font-mono ${milestoneTotal !== parseFloat(totalAmount) ? 'text-red-400' : 'text-green-400'}`}>
                                    {milestoneTotal.toLocaleString()} / {parseFloat(totalAmount || '0').toLocaleString()} {token?.symbol}
                                </div>
                                <div className="text-xs text-gray-400">Total Allocated</div>
                            </div>
                        </div>
                         <div className="pt-6 mt-6 border-t border-white/10">
                            <Button onClick={() => setStep('review')} disabled={!areMilestonesValid} className="w-full">Review Contract</Button>
                        </div>
                    </div>
                );
            case 'review':
                return (
                     <div>
                         <div className="flex items-center gap-4 mb-6">
                            <button onClick={() => setStep('milestones')} className="text-gray-400 hover:text-white"><ArrowLeft size={20}/></button>
                            <h3 className="font-serif text-2xl text-white">Review & Deploy</h3>
                        </div>
                        <div className="space-y-3 bg-white/5 p-4 rounded-lg border border-white/10 mb-6 text-sm">
                            <div className="flex justify-between"><span className="text-gray-400">Title:</span> <span className="font-bold text-white">{title}</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">Counterparty:</span> <span className="font-mono text-white truncate max-w-[180px]">{counterparty}</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">Total Value:</span> <span className="font-mono font-bold text-white">{parseFloat(totalAmount).toLocaleString()} {token?.symbol}</span></div>
                             <div className="flex justify-between"><span className="text-gray-400">Privacy:</span> <span className="font-bold text-white">{stealth ? 'Stealth Enabled' : 'Public'}</span></div>
                        </div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Milestones</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2 -mr-2 mb-6">
                            {milestones.map((m,i) => (
                                <div key={i} className="flex justify-between p-2 rounded-md bg-white/5 text-sm">
                                    <span className="text-gray-300">{i+1}. {m.title}</span>
                                    <span className="font-mono font-bold text-white">{parseFloat(m.amount).toLocaleString()} {token?.symbol}</span>
                                </div>
                            ))}
                        </div>
                        <div className="p-3 bg-blue-900/20 text-blue-300 text-xs rounded-lg border border-blue-500/20 flex items-start gap-2">
                            <Info size={16} className="mt-0.5 shrink-0"/>
                            <span>Deploying this contract will require a small gas fee and will lock <strong className="text-white">{totalAmount} {token?.symbol}</strong> from your wallet until the first milestone is released.</span>
                        </div>
                        <div className="pt-6 mt-6 border-t border-white/10">
                            <Button onClick={handleDeploy} isLoading={isProcessing} className="w-full" variant="primary">
                                {isProcessing ? 'Deploying...' : 'Deploy Contract'}
                            </Button>
                        </div>
                    </div>
                );
        }
    };
    
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 10 }} className="bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-lg p-6 relative border border-[#2D3142] overflow-hidden">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-20"><X size={20} /></button>
                <AnimatePresence mode="wait">
                    <motion.div key={step} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.2 }}>
                        {renderContent()}
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
};
